import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, Save, Bot, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import {
  aiSettingsService,
  type AiProvider, type TradeAuthorityMode,
  type UserAiSettings, type AdminAiSettings,
} from '@/services/aiSettingsService';

// ── shared field primitives ──────────────────────────────────────

const inputCls =
  'w-full h-9 rounded-md border border-input bg-background px-3 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-ring';

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground/80">{hint}</span>}
    </label>
  );
}

const TRADE_MODE_DESC: Record<TradeAuthorityMode, string> = {
  ProposeOnly: 'The AI never places an order — it proposes trades you approve or reject.',
  AutonomousPaper: 'The AI places paper-trading orders directly; live orders still need your approval.',
  FullyAutonomous: 'The AI places paper AND live orders directly, bounded by the kill switch and trade caps.',
};

// ── per-user AI settings ─────────────────────────────────────────

const UserAiSection: React.FC = () => {
  const [loaded, setLoaded] = useState<UserAiSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // form fields
  const [useSystemAi, setUseSystemAi] = useState(true);
  const [provider, setProvider] = useState<AiProvider>('OpenAi');
  const [apiKey, setApiKey] = useState('');           // blank = keep stored
  const [model, setModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [mode, setMode] = useState<TradeAuthorityMode>('ProposeOnly');
  const [disabledTools, setDisabledTools] = useState<string[]>([]);

  const hydrate = (s: UserAiSettings) => {
    setLoaded(s);
    setUseSystemAi(s.useSystemAi);
    setProvider(s.provider);
    setApiKey('');
    setModel(s.model ?? '');
    setBaseUrl(s.baseUrl ?? '');
    setMode(s.tradeAuthorityMode);
    setDisabledTools(s.disabledTools ?? []);
  };

  useEffect(() => {
    let cancelled = false;
    aiSettingsService.get()
      .then(s => { if (!cancelled) hydrate(s); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await aiSettingsService.update({
        useSystemAi,
        provider,
        apiKey: apiKey || undefined,   // blank → keep stored
        model: model || undefined,
        baseUrl: baseUrl || undefined,
        tradeAuthorityMode: mode,
        disabledTools,
      });
      hydrate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded && !error) {
    return (
      <Card className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading AI settings…
      </Card>
    );
  }

  const sys = loaded?.systemAi;

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Your AI assistant</h3>
      </div>

      {/* system-AI availability banner */}
      {sys && (
        <div className={`flex items-start gap-2 rounded-md border p-2 text-xs ${
          sys.available
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : 'border-amber-500/30 bg-amber-500/5'
        }`}>
          {sys.available
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
            : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />}
          <span className="text-muted-foreground">
            {sys.available
              ? `System AI available (${sys.provider}${sys.model ? ` · ${sys.model}` : ''}). You can use it without your own key.`
              : (sys.reason ?? 'System AI is not available.')}
          </span>
        </div>
      )}

      {/* source toggle */}
      <Field label="AI source">
        <Select value={useSystemAi ? 'system' : 'own'}
                onValueChange={(v) => setUseSystemAi(v === 'system')}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
            <SelectItem value="system" disabled={!sys?.available}>
              Use the system-provided AI
            </SelectItem>
            <SelectItem value="own">Bring my own API key</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {/* own-key fields */}
      {!useSystemAi && (
        <div className="space-y-3 rounded-md border border-border/60 p-3">
          <Field label="Provider">
            <Select value={provider} onValueChange={(v) => setProvider(v as AiProvider)}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                <SelectItem value="OpenAi">OpenAI</SelectItem>
                <SelectItem value="Anthropic">Anthropic</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field
            label="API key"
            hint={loaded?.hasOwnKey
              ? `A key is saved (${loaded.apiKeyMasked}). Leave blank to keep it.`
              : 'Your OpenAI or Anthropic API key.'}
          >
            <input type="password" className={inputCls} value={apiKey}
                   autoComplete="off" placeholder={loaded?.hasOwnKey ? '•••• keep existing' : 'sk-…'}
                   onChange={(e) => setApiKey(e.target.value)} />
          </Field>
          <Field label="Model" hint="e.g. gpt-4o or claude-sonnet-4-6">
            <input className={inputCls} value={model}
                   onChange={(e) => setModel(e.target.value)} placeholder="model id" />
          </Field>
          <Field label="Base URL (optional)" hint="For OpenAI-compatible proxies / Azure.">
            <input className={inputCls} value={baseUrl}
                   onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://…" />
          </Field>
        </div>
      )}

      {/* trade authority */}
      <Field label="Trade authority" hint={TRADE_MODE_DESC[mode]}>
        <Select value={mode} onValueChange={(v) => setMode(v as TradeAuthorityMode)}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
            <SelectItem value="ProposeOnly">Propose only — I approve every order</SelectItem>
            <SelectItem value="AutonomousPaper">Autonomous on paper, confirm for live</SelectItem>
            <SelectItem value="FullyAutonomous">Fully autonomous (paper + live)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {/* tool gate — restrict which tools the assistant may use */}
      {loaded && loaded.tools.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">Tools the assistant may use</span>
          <div className="rounded-md border border-border/60 divide-y divide-border/60">
            {loaded.tools.map((t) => {
              const on = !disabledTools.includes(t.name);
              return (
                <label key={t.name} className="flex items-start gap-2 p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={on}
                    className="mt-0.5"
                    onChange={(e) => setDisabledTools((prev) =>
                      e.target.checked
                        ? prev.filter((n) => n !== t.name)
                        : [...prev, t.name])}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium font-mono">{t.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{t.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <span className="block text-[11px] text-muted-foreground/80">
            Unchecked tools are hidden from the assistant.
          </span>
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
        {saved && <span className="text-xs text-emerald-500">Saved.</span>}
      </div>
    </Card>
  );
};

// ── system AI (SuperAdmin) ───────────────────────────────────────

const SystemAiSection: React.FC = () => {
  const [loaded, setLoaded] = useState<AdminAiSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<AiProvider>('OpenAi');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [minimumTier, setMinimumTier] = useState('Free');

  const hydrate = (s: AdminAiSettings) => {
    setLoaded(s);
    setEnabled(s.enabled);
    setProvider(s.provider);
    setApiKey('');
    setModel(s.model ?? '');
    setBaseUrl(s.baseUrl ?? '');
    setMinimumTier(s.minimumTier);
  };

  useEffect(() => {
    let cancelled = false;
    aiSettingsService.getSystem()
      .then(s => { if (!cancelled) hydrate(s); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await aiSettingsService.updateSystem({
        enabled, provider,
        apiKey: apiKey || undefined,
        model: model || undefined,
        baseUrl: baseUrl || undefined,
        minimumTier,
      });
      hydrate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded && !error) {
    return (
      <Card className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading system AI…
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">System AI (admin)</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        The platform's own AI key, offered to users who don't bring their own.
      </p>

      <Field label="Status">
        <Select value={enabled ? 'on' : 'off'} onValueChange={(v) => setEnabled(v === 'on')}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
            <SelectItem value="on">Enabled — offered to users</SelectItem>
            <SelectItem value="off">Disabled — users must bring their own key</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Provider">
        <Select value={provider} onValueChange={(v) => setProvider(v as AiProvider)}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
            <SelectItem value="OpenAi">OpenAI</SelectItem>
            <SelectItem value="Anthropic">Anthropic</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field
        label="API key"
        hint={loaded?.hasApiKey
          ? `A key is saved (${loaded.apiKeyMasked}). Leave blank to keep it.`
          : 'The platform API key for this provider.'}
      >
        <input type="password" className={inputCls} value={apiKey} autoComplete="off"
               placeholder={loaded?.hasApiKey ? '•••• keep existing' : 'sk-…'}
               onChange={(e) => setApiKey(e.target.value)} />
      </Field>
      <Field label="Model" hint="e.g. gpt-4o or claude-sonnet-4-6">
        <input className={inputCls} value={model}
               onChange={(e) => setModel(e.target.value)} placeholder="model id" />
      </Field>
      <Field label="Base URL (optional)">
        <input className={inputCls} value={baseUrl}
               onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://…" />
      </Field>
      <Field label="Minimum tier" hint="Lowest subscription tier allowed to use the system AI.">
        <Select value={minimumTier} onValueChange={setMinimumTier}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
            <SelectItem value="Free">Free — available to everyone</SelectItem>
            <SelectItem value="Basic">Basic and above</SelectItem>
            <SelectItem value="Pro">Pro and above</SelectItem>
            <SelectItem value="Enterprise">Enterprise only</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
        {saved && <span className="text-xs text-emerald-500">Saved.</span>}
      </div>
    </Card>
  );
};

// ── page ─────────────────────────────────────────────────────────

export const AiSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.includes('SuperAdmin') ?? false;

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">AI Assistant</h2>
        <p className="text-xs text-muted-foreground">
          Configure the AI used for analysis, guidance, and automated trading.
        </p>
      </div>
      <UserAiSection />
      {isSuperAdmin && <SystemAiSection />}
    </div>
  );
};

export default AiSettingsPage;
